import { clamp } from '@adrise/utils/lib/tools';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import { calculateAdSequenceCompletionProgress } from 'client/features/playback/old-vod/tools/adTools';
import OTTPlayerFireTVSimpleAdProgress from 'common/experiments/config/ottPlayerFireTVSimpleAdProgress';
import AdMessage, { AD_MESSAGE_STYLE } from 'common/features/playback/components/AdMessage/AdMessage';
import { getMessageData } from 'common/features/playback/components/AdMessageWrapper/getMessageData';
import useExperiment from 'common/hooks/useExperiment';
import type { StoreState } from 'common/types/storeState';

interface Props {
  adCount: number;
  adSequence: number;
  className?: string;
  duration: number;
  position: number;
  shouldUsePlayerPackage?: boolean;
  hideProgressBar?: boolean;
  remainingPodDuration?: number;
  hideText?: boolean;
}

const AdMessageWrapper: React.FunctionComponent<Props> = (props) => {
  const { adCount, adSequence, duration, position, hideProgressBar, remainingPodDuration, className, hideText } = props;
  const ottPlayerFireTVSimpleAdProgress = useExperiment(OTTPlayerFireTVSimpleAdProgress);
  const adProgressStyle = __IS_SLOW_PLATFORM__ ? AD_MESSAGE_STYLE.AD_PROGRESS_OF_CURRENT_AD : ottPlayerFireTVSimpleAdProgress.getValue();

  useEffect(() => {
    ottPlayerFireTVSimpleAdProgress.logExposure();
  }, [ottPlayerFireTVSimpleAdProgress]);

  const showProgressBar = !__IS_SLOW_PLATFORM__ && !hideProgressBar && adProgressStyle === AD_MESSAGE_STYLE.LEGACY;

  const getProgress = () => {
    if (!showProgressBar) {
      return 0;
    }

    const adSequenceCompletionProgress = calculateAdSequenceCompletionProgress(adCount);
    const alreadyCompleted = adSequenceCompletionProgress[adSequence - 1];
    let progressInCurrentSequence = 0;
    if (duration !== 0) {
      progressInCurrentSequence = (position / duration) * (adSequenceCompletionProgress[adSequence] - alreadyCompleted);
    }
    // rounding to get only two decimal points.
    return clamp(Math.round((progressInCurrentSequence + alreadyCompleted) * 100) / 100, 0, 1);
  };

  const messageData = hideText ? undefined : getMessageData({
    adProgressStyle,
    adSequence,
    adCount,
    duration,
    position,
    remainingPodDuration,
  });

  return <AdMessage progress={getProgress()} className={className} showProgressBar={showProgressBar} messageData={messageData} />;
};

export const mapStateToProps = (state: StoreState, { shouldUsePlayerPackage }: Partial<Props>) => {
  const { ottPlayer, player } = state;
  // check if OTT and not meant to use new player package then use ottPlayer if not use web
  const useOldPlayer = __ISOTT__ && !shouldUsePlayerPackage;
  const playerState = useOldPlayer ? ottPlayer : player;
  const {
    ad: { adCount, adSequence },
    adProgress: { duration, position, remainingPodDuration },
  } = playerState;

  return {
    adCount,
    adSequence,
    duration,
    position,
    remainingPodDuration,
  };
};

export default connect(mapStateToProps)(AdMessageWrapper);
